import groq from 'groq'

import { blocksFragment } from './blocks'
import { blogPostOptionsFragment, blogPostWithoutBodyFragment } from './blog'
import { contentFragment } from './content'
import { imageFragment } from './image'
import { muxVideoFragment, videoFragment } from './video'

const gridModuleFragment = groq`
  size,
  background,
  fullWidth,
  noColumnGaps,
  noRowGaps,
  reverseSequence,
  spacing,
  columns[] {
    _key,
    sizes[] {
      breakpoint,
      width,
      justify,
      align,
      start,
      justifyContent,
      alignContent,
    },
    blocks[] {
      ${blocksFragment}
    },
    spacing,
    background,
    frameColor,
    borderRadius,
    hideOverflow,
  }
`

const heroModuleFragment = groq`
  bgType,
  content[] {
    ${contentFragment}
  },
  contentPosition,
  showScrollIndicator,
  photos {
    mobilePhoto {
      ${imageFragment}
    },
    desktopPhoto {
      ${imageFragment}
    }
  },
  muxVideo {
    ${muxVideoFragment}
  },
  videoAspectRatio {
    custom,
    base,
    sm,
    md,
    lg,
    xl
  },
`

const heroCaseVideoCarouselModuleFragment = groq`
  items[] {
    content[] {
      ${contentFragment}
    },
    muxVideo {
      ${muxVideoFragment}
    },
    "casePage": case->{
      "id": _id,
      "type": _type,
      "slug": slug.current,
      "heroModule": modules[0] {
        _key,
        _type,
        _type == 'hero' => {
          ${heroModuleFragment}
        },
      },
    },
  }
`

const heroVideoSliderModuleFragment = groq`
  slides[] {
    content[] {
      ${contentFragment}
    },
    muxVideo {
      ${muxVideoFragment}
    },
  }
`

const dividerPhotoModuleFragment = groq`
  background,
  photo {
    ${imageFragment}
  }
`

const simpleMarqueeItemFragment = groq`
  text
`

const photoMarqueeItemFragment = groq`
  "photo": {
    ${imageFragment}
  }
`

const marqueeModuleFragment = groq`
  content[] {
    ${contentFragment}
  },
  contentAlignment,
  speed,
  reverse,
  pausable,
  items[] {
    _key,
    _type,
    _type == 'simple' => {
      ${simpleMarqueeItemFragment}
    },
    _type == 'photo' => {
      ${photoMarqueeItemFragment}
    },
  }
`

const getBlogPostGridModulePostsFragment = (limit?: number) => groq`
  "posts": *[
    _type == "blogPost" &&
    type != "template" &&
    locale == $locale &&
    (!defined(^.author) || author->slug.current == ^.author->slug.current) &&
    (!defined(^.category) || categories[]->slug.current match ^.category->slug.current)
  ] | order(date desc) | order(_createdAt desc) ${
    limit ? `[0..${limit - 1}]` : ''
  } {
    ${blogPostWithoutBodyFragment}
  },
`

const blogPostGridModuleFragment = groq`
  postsPerRow,
  limitPosts && postsPerRow == 3 => {
    ${getBlogPostGridModulePostsFragment(3)}
  },
  limitPosts && postsPerRow == 4 => {
    ${getBlogPostGridModulePostsFragment(4)}
  },
  !limitPosts || !defined(postsPerRow) => {
    ${getBlogPostGridModulePostsFragment()}
  },
  options {
    ${blogPostOptionsFragment}
  },
`

const blogPostHeaderModuleFragment = groq`
  "post": *[
    _type == "blogPost" &&
    type != "template" &&
    slug.current == $blogPostSlug &&
    locale == $blogLocale
  ]
    | order(_updatedAt desc)[0]
  {
    ${blogPostWithoutBodyFragment}
  },
  options {
    ${blogPostOptionsFragment}
  }
`

const blogPostBodyModuleFragment = groq`
  content[] {
    ${contentFragment}
  }
`

const blogCategoryGridModuleFragment = groq`
  options {
    ${blogPostOptionsFragment}
  }
`

const blogAuthorGridModuleFragment = groq`
  options {
    ${blogPostOptionsFragment}
  }
`

const largeMediaTextModuleFragment = groq`
  mediaType,
  image {
    ${imageFragment}
  },
  video {
    asset->
  },
  content[] {
    ${contentFragment}
  },
  frameColor,
`

const caseOverviewModuleFragment = groq`
  title,
  filterByTags,
  "cases": *[
    _type == "case" &&
    type == "page" &&
    locale == $locale
  ] {
    title,
    "slug": slug.current,
    coverMediaType,
    coverImage {
      ${imageFragment}
    },
    coverVideo {
      ${muxVideoFragment}
    },
    tags[] {
      label,
      value,
    },
  },
`

export const modulesFragment = groq`
  _key,
  _type,
  _type == 'grid' => {
    ${gridModuleFragment}
  },
  _type == 'hero' => {
    ${heroModuleFragment}
  },
  _type == 'heroCaseVideoCarousel' => {
    ${heroCaseVideoCarouselModuleFragment}
  },
  _type == 'heroVideoSlider' => {
    ${heroVideoSliderModuleFragment}
  },
  _type == 'dividerPhoto' => {
    ${dividerPhotoModuleFragment}
  },
  _type == 'video' => {
    ${videoFragment}
  },
  _type == 'marquee' => {
    ${marqueeModuleFragment}
  },
  _type == 'blogPostGrid' => {
    ${blogPostGridModuleFragment}
  },
  _type == 'blogPostHeader' => {
    ${blogPostHeaderModuleFragment}
  },
  _type == 'blogPostBody' => {
    ${blogPostBodyModuleFragment}
  },
  _type == 'blogPostReadMore' => {},
  _type == 'blogCategoryPostGrid' => {
    ${blogCategoryGridModuleFragment}
  },
  _type == 'blogAuthorPostGrid' => {
    ${blogAuthorGridModuleFragment}
  },
  _type == 'largeMediaText' => {
    ${largeMediaTextModuleFragment}
  },
  _type == 'caseOverview' => {
    ${caseOverviewModuleFragment}
  },
`
